/**
 * @file api.ts
 * @brief Super Admin → Integrations (W32): GET / PUT /api/admin/integrations
 *        platform + companies (BACKEND B42, 30-10-2026)
 *
 * @details
 * A backend before B42 answers 404: the page says so and offers no switch.
 * A platform save also refreshes the public config this page load uses, so
 * the Settings → Integrations cards follow at once.
 *
 * @author DES (prepared with Claude)
 * @date 30-10-2026
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiClientError, apiGet, apiPut } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import { companyFeaturesKeys } from "@/features/company-features/api";
import { __resetPublicConfig } from "@/shared/publicConfig/publicConfig";
import { publicConfigKey } from "@/shared/publicConfig/usePublicConfig";
import {
  parseCompaniesPage,
  parseCompany,
  parsePlatform,
  type IntegrationApp,
  type IntegrationCompaniesPage,
  type IntegrationCompany,
  type PlatformApps,
} from "./integrationSwitches";

export const integrationSwitchKeys = {
  all: ["admin", "integrations"] as const,
  platform: ["admin", "integrations", "platform"] as const,
  companies: (search: string, page: number, limit: number) =>
    ["admin", "integrations", "companies", search, page, limit] as const,
};

export type PlatformState = { status: "ok"; apps: PlatformApps } | { status: "missing" };

export function useIntegrationPlatform() {
  return useQuery<PlatformState>({
    queryKey: integrationSwitchKeys.platform,
    retry: false,
    queryFn: async () => {
      try {
        return { status: "ok", apps: parsePlatform(await apiGet<unknown>(adminEndpoints.integrationsPlatform)) };
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return { status: "missing" };
        throw err;
      }
    },
  });
}

export function useSetIntegrationPlatform() {
  const qc = useQueryClient();
  return useMutation<PlatformApps, Error, Partial<Record<IntegrationApp, boolean>>>({
    mutationFn: async (patch) => parsePlatform(await apiPut<unknown>(adminEndpoints.integrationsPlatform, patch)),
    onSuccess: (apps) => {
      const next: PlatformState = { status: "ok", apps };
      qc.setQueryData<PlatformState>(integrationSwitchKeys.platform, next);
      __resetPublicConfig();
      void qc.invalidateQueries({ queryKey: publicConfigKey });
    },
  });
}

export function useIntegrationCompanies(search: string, page: number, limit: number) {
  return useQuery<IntegrationCompaniesPage>({
    queryKey: integrationSwitchKeys.companies(search, page, limit),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      parseCompaniesPage(
        await apiGet<unknown>(adminEndpoints.integrationsCompanies, {
          params: { search: search || undefined, page, limit },
        }),
      ),
  });
}

export function useSetCompanyIntegration() {
  const qc = useQueryClient();
  return useMutation<IntegrationCompany, Error, { id: string; patch: Partial<Record<IntegrationApp, boolean>> }>({
    mutationFn: async ({ id, patch }) =>
      parseCompany((await apiPut<{ company?: unknown }>(adminEndpoints.integrationsCompany(id), patch)).company),
    onSuccess: (company) => {
      // Put the saved row in every loaded page, then refetch the counts.
      qc.setQueriesData<IntegrationCompaniesPage>(
        { queryKey: [...integrationSwitchKeys.all, "companies"] },
        (prev) =>
          prev ? { ...prev, companies: prev.companies.map((c) => (c.id === company.id ? company : c)) } : prev,
      );
      void qc.invalidateQueries({ queryKey: integrationSwitchKeys.all });
      void qc.invalidateQueries({ queryKey: companyFeaturesKeys.all });
    },
  });
}
